import { htmlToPlainText } from './richMailModel';

/**
 * ТЗ 0112 (#156) — показ писем в коммуникаторе: распознать HTML в теле
 * text/plain и свернуть цитату прошлой переписки.
 *
 * Всё здесь — чистые функции без React: проверяются jest-ом и одинаково
 * работают в коммуникаторе и в переписке договора/лида.
 */

/**
 * Теги, по которым plain-тело опознаётся как исходник HTML (формы сайтов,
 * PHP `mail()` кладут вёрстку в часть text/plain). Одиночный `<b>` в прозе
 * встречается, поэтому нужно хотя бы два тега — открывающий и закрывающий.
 */
const HTML_TAG_RE = /<\/?(?:html|head|body|div|p|br|table|thead|tbody|tr|td|th|span|a|img|ul|ol|li|h[1-6]|strong|b|i|em|u|font|center|style|blockquote|hr)\b[^<>]*>/gi;
const HTML_DOC_RE = /<!doctype\s+html|<html[\s>]|<body[\s>]/i;

/** Похоже ли plain-тело письма на исходник HTML. */
export const looksLikeHtml = (text?: string | null): boolean => {
  const value = text || '';
  if (!value.includes('<')) return false;
  if (HTML_DOC_RE.test(value)) return true;
  const tags = value.match(HTML_TAG_RE);
  return Boolean(tags && tags.length >= 2);
};

/**
 * HTML, который показываем во фрейме: серверный `bodyHtml`, а если его нет
 * и plain-тело само оказалось вёрсткой — оно (фрейм прогонит его через
 * DOMPurify, как любое письмо). `null` — показываем текстом.
 */
export const emailDisplayHtml = (
  body?: string | null,
  bodyHtml?: string | null,
): string | null => {
  if (bodyHtml && bodyHtml.trim()) return bodyHtml;
  if (looksLikeHtml(body)) return body || null;
  return null;
};

/**
 * Строка-атрибуция цитаты: «On … wrote:», «W dniu … napisał(a):»,
 * «… написал(а):», разделитель Outlook «-----Original Message-----».
 */
const ATTRIBUTION_RES: RegExp[] = [
  /^on\b[\s\S]{1,300}\bwrote:$/i,
  /^w\s+dniu\b[\s\S]{1,300}\bnapisał(?:\(a\)|a)?:$/i,
  /^[\s\S]{0,300}\s(?:написал|написала|написал\(а\)|пишет):$/i,
  /^-{2,}\s*(?:original message|wiadomość oryginalna|исходное сообщение|forwarded message|przekazana wiadomość|пересылаемое сообщение)\s*-{2,}$/i,
];

/** Похож ли (обрезанный) текст на строку-атрибуцию цитаты. */
export const isQuoteAttribution = (text: string): boolean => {
  const value = text.replace(/\s+/g, ' ').trim();
  if (!value || value.length > 400) return false;
  return ATTRIBUTION_RES.some((re) => re.test(value));
};

export interface IQuoteSplit {
  /** Новое в письме — показывается сразу. */
  main: string;
  /** Цитата прошлой переписки — по кнопке; `null` — цитаты нет. */
  quote: string | null;
}

const QUOTE_SELECTOR = [
  '.gmail_quote',
  'blockquote[type="cite"]',
  '.yahoo_quoted',
  '.moz-cite-prefix',
  '#divRplyFwdMsg',
  '#appendonsend',
].join(',');

const hasContent = (html: string): boolean =>
  Boolean(htmlToPlainText(html).trim()) || /<img\b/i.test(html);

/** Первый в порядке документа из двух узлов. */
const earlier = (a: Node | null, b: Node | null): Node | null => {
  if (!a) return b;
  if (!b) return a;
  // eslint-disable-next-line no-bitwise
  return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
    ? a
    : b;
};

/** Текстовый узел-атрибуция → самый внешний предок, который с неё начинается. */
const attributionStart = (node: Text, body: HTMLElement): Node => {
  const lead = node.data.trim();
  let start: Node = node;
  let parent = node.parentElement;
  while (
    parent &&
    parent !== body &&
    (parent.textContent || '').trim().startsWith(lead)
  ) {
    start = parent;
    parent = parent.parentElement;
  }
  return start;
};

/**
 * HTML письма → новое + цитата. Цитата — от первого из: `.gmail_quote`,
 * `blockquote[type=cite]` (Apple Mail, Thunderbird), обёрток Yahoo/Outlook,
 * строки «On … wrote:» — и до конца письма. Если над цитатой ничего нет
 * (письмо — сплошная пересылка), не сворачиваем: показать было бы нечего.
 */
export const splitEmailQuote = (html: string): IQuoteSplit => {
  const none = { main: html, quote: null };
  if (!html || typeof DOMParser === 'undefined') return none;
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
  const { body } = doc;
  let start: Node | null = body.querySelector(QUOTE_SELECTOR);

  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (start && earlier(node, start) === start) break;
    const text = node as Text;
    if (isQuoteAttribution(text.data)) {
      start = earlier(attributionStart(text, body), start);
      break;
    }
  }
  if (!start || start === body) return none;

  // Строка-атрибуция прямо над `blockquote` (Thunderbird и др.) — в цитату.
  let prev = start.previousSibling;
  while (prev && prev.nodeType === 3 && !(prev.textContent || '').trim()) {
    prev = prev.previousSibling;
  }
  if (prev && isQuoteAttribution(prev.textContent || '')) start = prev;

  const range = doc.createRange();
  range.setStartBefore(start);
  range.setEndAfter(body.lastChild as Node);
  const holder = doc.createElement('div');
  holder.appendChild(range.extractContents());
  const main = body.innerHTML;
  const quote = holder.innerHTML;
  if (!hasContent(main) || !hasContent(quote)) return none;
  return { main, quote };
};

const QUOTED_LINE_RE = /^\s*>/;

/**
 * Plain-text письмо → новое + цитата: хвост из строк с `>` (и пустых) в
 * конце письма вместе со строкой-атрибуцией над ним.
 */
export const splitPlainQuote = (text: string): IQuoteSplit => {
  const none = { main: text, quote: null };
  if (!text) return none;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  let start = lines.length;
  let quoted = 0;
  while (
    start > 0 &&
    (!lines[start - 1].trim() || QUOTED_LINE_RE.test(lines[start - 1]))
  ) {
    if (lines[start - 1].trim()) quoted += 1;
    start -= 1;
  }
  if (!quoted) return none;
  // Атрибуция — одна строка или перенесённая на две (Gmail режет на 76).
  let prev = start - 1;
  while (prev >= 0 && !lines[prev].trim()) prev -= 1;
  if (prev >= 0) {
    if (isQuoteAttribution(lines[prev])) {
      start = prev;
    } else if (
      prev > 0 &&
      isQuoteAttribution(`${lines[prev - 1]} ${lines[prev]}`)
    ) {
      start = prev - 1;
    }
  }
  const main = lines.slice(0, start).join('\n').replace(/\s+$/, '');
  if (!main.trim()) return none;
  return { main, quote: lines.slice(start).join('\n').replace(/^\n+/, '') };
};
